import { useState, useEffect } from 'react';
import { agenciesService, AdminAgencyRecord } from '@/services/agencies';
import { plansService, AdminPlan } from '@/services/plans';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Info, Loader2, RefreshCw } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import toastService from '@/services/toast/service';
import { isManualAccessRefusal, modalManualAccess, stripeStatusField } from '@/lib/manualAccess';

const STRIPE_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'free', label: 'Free (no subscription)' },
  { value: 'active', label: 'Active' },
  { value: 'trialing', label: 'Trialing' },
  { value: 'past_due', label: 'Past Due' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'incomplete', label: 'Incomplete' },
  { value: 'incomplete_expired', label: 'Incomplete Expired' },
  { value: 'paused', label: 'Paused' },
  { value: 'canceled', label: 'Cancelled' },
];

interface EditAgencyModalProps {
  agency: AdminAgencyRecord;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function EditAgencyModal({ agency, open, onOpenChange, onSuccess }: EditAgencyModalProps) {
  const [stripeCustomerId, setStripeCustomerId] = useState(agency.stripeCustomerId || '');
  const [stripeSubscriptionId, setStripeSubscriptionId] = useState(agency.stripeSubscriptionId || '');
  const [selectedPlanId, setSelectedPlanId] = useState<string | undefined>(agency.planId?.toString() || undefined);
  const [stripeStatus, setStripeStatus] = useState<string>(agency.stripeStatus || 'free');
  // The Stripe status, customer and subscription id as stored, before any Sync or edit.
  const [storedStripe, setStoredStripe] = useState({
    customerId: agency.stripeCustomerId || '',
    status: agency.stripeStatus || 'free',
    subscriptionId: agency.stripeSubscriptionId || '',
  });
  const [manualAccessReason, setManualAccessReason] = useState(agency.manualAccessReason || '');
  const [onboardingCompleted, setOnboardingCompleted] = useState(true);
  const [markOnboardingCompleted, setMarkOnboardingCompleted] = useState(false);
  const [brandingPolicy, setBrandingPolicy] = useState<'legacy' | 'always_on'>(agency.brandingPolicy ?? 'legacy');
  const [loadedBrandingPolicy, setLoadedBrandingPolicy] = useState<'legacy' | 'always_on'>(agency.brandingPolicy ?? 'legacy');
  const [plans, setPlans] = useState<AdminPlan[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncedPlan, setSyncedPlan] = useState<{ id: number; displayName: string } | null>(null);
  const [syncedPlanUnknown, setSyncedPlanUnknown] = useState(false);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [isLoadingAgency, setIsLoadingAgency] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoadingAgency(true);
        setIsLoadingPlans(true);
        const [loadedPlans, loadedAgency] = await Promise.all([
          plansService.getPlans(),
          agenciesService.getAgency(agency.id)
        ]);
        setPlans(loadedPlans);
        setStripeCustomerId(loadedAgency.stripeCustomerId || '');
        setStripeSubscriptionId(loadedAgency.stripeSubscriptionId || '');
        setSelectedPlanId(loadedAgency.planId?.toString() || undefined);
        setStripeStatus(loadedAgency.stripeStatus || 'free');
        setBrandingPolicy(loadedAgency.brandingPolicy ?? 'legacy');
        setLoadedBrandingPolicy(loadedAgency.brandingPolicy ?? 'legacy');
        setStoredStripe({
          customerId: loadedAgency.stripeCustomerId || '',
          status: loadedAgency.stripeStatus || 'free',
          subscriptionId: loadedAgency.stripeSubscriptionId || '',
        });
        setManualAccessReason(loadedAgency.manualAccessReason || '');
        setOnboardingCompleted(loadedAgency.onboardingCompletedAtUtc != null);
        setMarkOnboardingCompleted(false);
        setSyncedPlan(null);
        setSyncedPlanUnknown(false);
      } catch (err) {
        toastService.error("Couldn't load agency details");
      } finally {
        setIsLoadingPlans(false);
        setIsLoadingAgency(false);
      }
    };
    if (open) {
      loadData();
    }
  }, [open, agency.id]);

  // Mirrors the server-side guard in PATCH /admin/agencies/[id]: a trialing status with
  // no trial end date is invisible to both get_admin_active_trials() and the day-7
  // reminder cron, and with no subscription there is nothing to pull those dates from.
  // Both fields are read-only, so this only fires for an agency already stored that way:
  // it explains why Save is blocked and that pasting the subscription id is the fix.
  const trialingWithoutSubscription = stripeStatus === 'trialing' && !stripeSubscriptionId.trim();

  // Warnings, not blocks: a plan that disagrees with the subscription means the agency
  // is entitled to something it is not paying for, or the reverse. Only known after a sync.
  const selectedPlan = plans.find((plan) => plan.id.toString() === selectedPlanId);
  const planDisagreesWithSubscription =
    !!syncedPlan && !!selectedPlanId && syncedPlan.id.toString() !== selectedPlanId;

  // Fills the status and customer id from the live subscription so they can be read
  // before saving. The trial window, period end and hasPaidBefore are pulled server
  // side on save; they have no inputs here because nobody should be typing them.
  // Manual access, mirroring the save (PATCH /admin/agencies/[id]): a paid plan with no
  // live subscription behind it shows as Manual and needs a reason.
  const manualAccess = modalManualAccess({
    planName: selectedPlan?.name,
    subscriptionId: stripeSubscriptionId,
    storedSubscriptionId: storedStripe.subscriptionId,
    synced: syncedPlan !== null || syncedPlanUnknown,
    stripeStatus,
  });
  const manualAccessReasonMissing = manualAccess && !manualAccessReason.trim();
  const statusField = stripeStatusField({ subscriptionId: stripeSubscriptionId, stripeStatus });

  const handleSync = async () => {
    const subscriptionId = stripeSubscriptionId.trim();
    if (!subscriptionId) {
      toastService.error('Add a Stripe subscription ID first');
      return;
    }

    setIsSyncing(true);
    try {
      const live = await agenciesService.previewStripeSubscription(agency.id, subscriptionId);
      setStripeStatus(live.status);
      setStripeCustomerId(live.customerId);
      setSyncedPlan(live.matchedPlan);
      setSyncedPlanUnknown(live.matchedPlan === null);
      toastService.success('Pulled from Stripe');
    } catch (err: any) {
      toastService.error(err.response?.data?.message || "Couldn't reach Stripe");
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await agenciesService.updateAgencyStripeRecords(
        agency.id,
        stripeCustomerId,
        stripeSubscriptionId,
        stripeStatus === 'free' ? '' : stripeStatus,
        selectedPlanId ? parseInt(selectedPlanId, 10) : undefined,
        markOnboardingCompleted,
        brandingPolicy !== loadedBrandingPolicy ? brandingPolicy : undefined,
        manualAccess ? manualAccessReason.trim() : undefined
      );
      toastService.success('Agency Stripe records updated');
      onSuccess();
      onOpenChange(false);
    } catch (err: any) {
      const message: string | undefined = err.response?.data?.message;
      toastService.error(message || "Couldn't update agency records");
      // The save went by the subscription's live Stripe status, which this form only
      // learns from Sync: pull it, so the manual access notice and reason field show.
      if (isManualAccessRefusal(message) && stripeSubscriptionId.trim()) await handleSync();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Agency</DialogTitle>
            <DialogDescription>
              Update the details for this agency.
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-4">
            {isLoadingAgency ? (
              <div className="flex flex-col items-center justify-center py-8">
                <p className="text-sm text-muted-foreground">Loading...</p>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="plan">ROASForm Plan</Label>
                  <Select
                    value={selectedPlanId}
                    onValueChange={setSelectedPlanId}
                    disabled={isSubmitting || isLoadingPlans}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={isLoadingPlans ? "Loading plans..." : "Select a plan"} />
                    </SelectTrigger>
                    <SelectContent>
                      {plans.map((plan) => (
                        <SelectItem key={plan.id} value={plan.id.toString()}>
                          {plan.displayName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {manualAccess && (
                    <div role="note" className="rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/50 dark:bg-amber-900/20">
                      <div className="flex items-start gap-2">
                        <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" />
                        <div className="space-y-1.5">
                          <p className="flex flex-wrap items-center gap-1.5 text-sm font-semibold text-amber-900 dark:text-amber-200">
                            This agency will get the <Badge tone="accent">Manual</Badge> badge
                          </p>
                          <p className="text-xs text-amber-800 dark:text-amber-300">
                            {selectedPlan?.displayName} is a paid plan with no Stripe subscription, so nothing will bill for it. It shows as Manual in the backoffice and as Billing Status manual in GHL, until they subscribe.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  {planDisagreesWithSubscription && (
                    <p className="text-xs text-amber-600 dark:text-amber-500">
                      The subscription is for {syncedPlan?.displayName}, not {selectedPlan?.displayName ?? 'the selected plan'}
                    </p>
                  )}
                  {syncedPlanUnknown && (
                    <p className="text-xs text-amber-600 dark:text-amber-500">
                      The subscription price is not linked to any ROASForm plan
                    </p>
                  )}
                </div>

                {manualAccess && (
                  <div className="space-y-2">
                    <Label htmlFor="manualAccessReason">
                      Reason for manual access <span className="text-destructive" aria-hidden="true">*</span>
                    </Label>
                    <Input
                      id="manualAccessReason"
                      value={manualAccessReason}
                      onChange={(e) => setManualAccessReason(e.target.value)}
                      placeholder="e.g. Old platform, Team, Partner"
                      maxLength={200}
                      required
                      disabled={isSubmitting}
                    />
                    <p className="text-xs text-muted-foreground">
                      Shown on the Manual badge. Removed automatically once they subscribe.
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="stripeSubscriptionId">Stripe Subscription ID</Label>
                  <div className="flex gap-2">
                    <Input
                      id="stripeSubscriptionId"
                      value={stripeSubscriptionId}
                      onChange={(e) => {
                        setStripeSubscriptionId(e.target.value);
                        // A Sync only describes the id it ran against, so editing the id
                        // puts the stored status and customer id back.
                        setStripeStatus(storedStripe.status);
                        setStripeCustomerId(storedStripe.customerId);
                        setSyncedPlan(null);
                        setSyncedPlanUnknown(false);
                      }}
                      placeholder="sub_..."
                      disabled={isSubmitting}
                    />
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={handleSync}
                          disabled={isSubmitting || isSyncing}
                          aria-label="Pull from Stripe"
                        >
                          {isSyncing ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <RefreshCw className="h-4 w-4" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Pulls the Stripe status and customer ID for this subscription</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="stripeCustomerId">Stripe Customer ID</Label>
                  <Input
                    id="stripeCustomerId"
                    value={stripeCustomerId}
                    placeholder="cus_..."
                    disabled
                    readOnly
                  />
                  <p className="text-xs text-muted-foreground">
                    Pulled from the subscription above
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="stripeStatus">Stripe Status</Label>
                  <Select value={statusField.shown} disabled>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a status" />
                    </SelectTrigger>
                    <SelectContent>
                      {STRIPE_STATUS_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {trialingWithoutSubscription ? (
                    <p className="text-xs text-destructive">
                      Trialing needs a Stripe subscription ID so the trial dates can be pulled in
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {statusField.helper}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="brandingPolicy">Badge policy</Label>
                  <Select value={brandingPolicy} onValueChange={(v) => setBrandingPolicy(v as 'legacy' | 'always_on')}>
                    <SelectTrigger id="brandingPolicy"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="legacy">Legacy (badge can be hidden on paid plans)</SelectItem>
                      <SelectItem value="always_on">Always on (large on Free, small on paid)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {!onboardingCompleted && (
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="markOnboardingCompleted"
                      checked={markOnboardingCompleted}
                      onCheckedChange={(checked) => setMarkOnboardingCompleted(checked === true)}
                      disabled={isSubmitting}
                      className="mt-0.5"
                    />
                    <div className="space-y-1">
                      <Label htmlFor="markOnboardingCompleted">Mark onboarding as completed</Label>
                      <p className="text-xs text-muted-foreground">
                        The user skips the in-app onboarding wizard and the assigned plan shows in backoffice.
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting || isLoadingAgency}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || isLoadingAgency || trialingWithoutSubscription || manualAccessReasonMissing}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
